import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Cropper from 'react-easy-crop';
import { supabase } from '../supabaseClient';
import { getEmailService, EmailService } from '../utils/emailService';
import { getCroppedImg, compressImage } from '../utils/imageUtils';
import { exportToCSV, generateUserReportPDF } from '../utils/exportUtils';
import { seedDatabase } from '../utils/seedData';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiSettings, FiMail, FiUsers, FiFileText, FiGlobe, FiCheck, FiX,
  FiEdit2, FiTrash2, FiPlus, FiEye, FiDownload, FiLoader, FiLogOut,
  FiRefreshCw, FiSave, FiMenu, FiX: FiClose, FiUserPlus, FiActivity,
  FiMoon, FiImage, FiUpload, FiDatabase, FiCode
} = FiIcons;

function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Auth State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  // Login Form State
  const [loginError, setLoginError] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const activeTab = searchParams.get('tab') || 'overview';
  const activeSubTab = searchParams.get('subtab') || (activeTab === 'settings' ? 'email' : 'user-reports');
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Preferences
  const [preferences, setPreferences] = useState({ darkMode: false });

  // Data State
  const [assessments, setAssessments] = useState([]);
  const [pendingEmails, setPendingEmails] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  
  // Seeder State
  const [seedStatus, setSeedStatus] = useState(null);

  // Settings State
  const [settings, setSettings] = useState({
    postmark_config: { apiKey: '', fromEmail: '', fromName: '', enabled: false },
    webhook_config: { enabled: false, endpoints: [] },
    email_notifications: { enabled: true },
    notification_templates: [],
    org_report_config: { default_threshold: 5 }
  });

  // Action State
  const [testEmail, setTestEmail] = useState('');
  const [testEmailStatus, setTestEmailStatus] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'collaborator', name: '' });
  const [editingMember, setEditingMember] = useState(null);
  const [editingOrgThreshold, setEditingOrgThreshold] = useState(null);

  // Profile & Cropping State
  const [profileForm, setProfileForm] = useState({ avatarUrl: '', newPassword: '', confirmPassword: '' });
  const fileInputRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  // Initialize
  useEffect(() => {
    checkAuthStatus();
    const savedPrefs = localStorage.getItem('admin_preferences');
    if (savedPrefs) {
      setPreferences(JSON.parse(savedPrefs));
    }
  }, []);

  // Apply Dark Mode
  useEffect(() => {
    if (preferences.darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('admin_preferences', JSON.stringify(preferences));
  }, [preferences.darkMode]);

  const checkAuthStatus = async () => {
    try {
      const demoSession = localStorage.getItem('admin_demo_session');
      if (demoSession === 'authenticated') {
        const demoUser = JSON.parse(localStorage.getItem('admin_demo_user') || '{}');
        setCurrentUser(demoUser);
        setIsAuthenticated(true);
        setIsDemoMode(true);
        loadAdminData();
        setIsLoading(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const { data: adminData } = await supabase
          .from('admin_users')
          .select('*')
          .eq('email', session.user.email)
          .eq('is_active', true)
          .single();

        if (adminData) {
          setCurrentUser({ ...adminData, avatar_url: adminData.avatar_url || '' });
          setIsAuthenticated(true);
          setIsDemoMode(false);
          loadAdminData();
        } else {
          await supabase.auth.signOut();
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAdminData = async () => {
    try {
      const [assessmentsRes, settingsRes, emailsRes, membersRes, orgsRes] = await Promise.all([
        supabase.from('assessments').select('*').order('created_at', { ascending: false }).limit(100),
        supabase.from('settings').select('*'),
        supabase.from('pending_emails').select('*').order('created_at', { ascending: false }).limit(20),
        supabase.from('admin_users').select('*').order('created_at', { ascending: false }),
        supabase.from('organizations').select('*')
      ]);

      setAssessments(assessmentsRes.data || []);

      const settingsObj = {};
      settingsRes.data?.forEach(s => settingsObj[s.key] = s.value);
      setSettings(prev => ({
        ...prev,
        ...settingsObj,
        org_report_config: settingsObj.org_report_config || { default_threshold: 5 },
        notification_templates: settingsObj.notification_templates || []
      }));

      setPendingEmails(emailsRes.data || []);
      setTeamMembers(membersRes.data || []);
      setOrganizations(orgsRes.data || []);
    } catch (error) {
      console.error('Data load error:', error);
      if (isDemoMode) {
        setTeamMembers([{ email: 'david.macauley@wend.works', name: 'David Macauley', role: 'super_admin', is_active: true }]);
      }
    }
  };

  const refreshData = async () => {
    setIsLoading(true);
    await loadAdminData();
    setIsLoading(false);
  };

  const saveSettings = async () => {
    try {
      for (const [key, value] of Object.entries(settings)) {
        await supabase.from('settings').upsert({ key, value });
      }
      alert('Settings saved successfully!');
    } catch (error) {
      alert('Error saving settings.');
    }
  };

  // --- Seed Data Handler ---
  const handleSeedData = async () => {
    if (!window.confirm('This will add fake data to your live database. Continue?')) return;
    setSeedStatus('Seeding...');
    const result = await seedDatabase();
    if (result.success) {
      setSeedStatus('✅ Done! Refreshing data...');
      await loadAdminData();
      setTimeout(() => setSeedStatus(null), 3000);
    } else {
      setSeedStatus(`❌ Error: ${result.error?.message}`);
    }
  };

  // --- Org Stats Logic ---
  const getOrgStats = () => {
    const stats = {};
    // Initialize with known organizations from DB
    organizations.forEach(org => {
      stats[org.code] = {
        code: org.code,
        name: org.name || 'Unnamed Org',
        threshold: org.response_threshold || settings.org_report_config.default_threshold,
        count: 0,
        scoreSum: 0,
        lastActivity: org.created_at
      };
    });

    // Aggregate assessments
    assessments.forEach(a => {
      const code = a.org_code || 'No Code';
      if (!stats[code]) {
        // Handle ad-hoc codes not in organization table
        stats[code] = {
          code,
          name: 'Ad-hoc Organization',
          threshold: settings.org_report_config.default_threshold,
          count: 0,
          scoreSum: 0,
          lastActivity: a.created_at
        };
      }
      stats[code].count++;
      stats[code].scoreSum += (a.score || 0);
      if (new Date(a.created_at) > new Date(stats[code].lastActivity)) {
        stats[code].lastActivity = a.created_at;
      }
    });

    return Object.values(stats);
  };

  const handleUpdateOrgThreshold = async (orgCode, newThreshold) => {
    try {
      const { error } = await supabase.from('organizations').upsert({
        code: orgCode,
        response_threshold: parseInt(newThreshold)
      }, { onConflict: 'code' });

      if (error) throw error;
      const { data } = await supabase.from('organizations').select('*');
      setOrganizations(data);
      setEditingOrgThreshold(null);
    } catch (e) {
      alert('Failed to update threshold: ' + e.message);
    }
  };

  // --- Export Logic ---
  const handleExportCSV = () => {
    const data = assessments.map(a => ({
      Date: new Date(a.created_at).toLocaleDateString(),
      Name: a.user_name,
      Email: a.user_email,
      Organization: a.org_code,
      Score: a.score,
      ...a.responses
    }));
    exportToCSV(data, `Assessment_Export_${new Date().toISOString().split('T')[0]}.csv`);
  };

  // --- Profile Logic ---
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (profileForm.newPassword && profileForm.newPassword !== profileForm.confirmPassword) {
      alert("Passwords don't match");
      return;
    }

    if (profileForm.avatarUrl) {
      const { error } = await supabase
        .from('admin_users')
        .update({ avatar_url: profileForm.avatarUrl })
        .eq('email', currentUser.email);

      if (!error) {
        setCurrentUser(prev => ({ ...prev, avatar_url: profileForm.avatarUrl }));
        if (isDemoMode) {
          const demoUser = JSON.parse(localStorage.getItem('admin_demo_user') || '{}');
          demoUser.avatar_url = profileForm.avatarUrl;
          localStorage.setItem('admin_demo_user', JSON.stringify(demoUser));
        }
      }
    }
    alert('Profile updated successfully');
    setShowProfileModal(false);
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
        setShowCropper(true);
      });
      reader.readAsDataURL(file);
    }
  };

  const handleSaveCrop = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
      const compressedImage = await compressImage(croppedImage, 500000);
      setProfileForm(prev => ({ ...prev, avatarUrl: compressedImage }));
      setShowCropper(false);
      setImageSrc(null);
    } catch (e) {
      console.error(e);
      alert('Error cropping image');
    }
  };

  // --- Login/Nav Helpers ---
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoading(true);

    try {
      if (loginForm.email === 'david.macauley@wend.works' && loginForm.password === 'admin123') {
        const demoUser = {
          email: 'david.macauley@wend.works',
          name: 'David Macauley',
          role: 'super_admin',
          avatar_url: ''
        };
        localStorage.setItem('admin_demo_session', 'authenticated');
        localStorage.setItem('admin_demo_user', JSON.stringify(demoUser));
        setCurrentUser(demoUser);
        setIsAuthenticated(true);
        setIsDemoMode(true);
        await loadAdminData();
        return;
      }

      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('*')
        .eq('email', loginForm.email)
        .eq('is_active', true)
        .single();

      if (adminError || !adminData) {
        setLoginError('Access denied.');
        return;
      }

      const { error } = await supabase.auth.signInWithOtp({
        email: loginForm.email,
        options: {
          emailRedirectTo: `${window.location.origin}/admin`
        }
      });

      if (error) throw error;
      setLoginError('Magic link sent!');
    } catch (error) {
      setLoginError('Login failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem('admin_demo_session');
    localStorage.removeItem('admin_demo_user');
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setCurrentUser(null);
    navigate('/admin');
  };

  const handleNavClick = (tabId, subTabId = null) => {
    let url = `/admin?tab=${tabId}`;
    if (subTabId) url += `&subtab=${subTabId}`;
    setSearchParams(subTabId ? { tab: tabId, subtab: subTabId } : { tab: tabId });
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (isLoading && !isAuthenticated) return <div className="min-h-screen flex items-center justify-center"><FiLoader className="w-8 h-8 animate-spin text-blue-600" /></div>;

  if (!isAuthenticated) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <h1 className="text-3xl font-bold text-center mb-6">Admin Login</h1>
        <form onSubmit={handleLogin} className="space-y-6">
          <input type="email" value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="admin@example.com" required />
          <input type="password" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} className="w-full px-4 py-3 border rounded-lg" placeholder="Password" required />
          {loginError && <p className="text-red-600 text-sm">{loginError}</p>}
          <button type="submit" className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700">Sign In</button>
        </form>
      </motion.div>
    </div>
  );

  return (
    <div className={`min-h-screen flex transition-colors duration-200 ${preferences.darkMode ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white dark:bg-gray-800 shadow-xl transition-all duration-300 flex flex-col z-20`}>
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
          {sidebarOpen && <h1 className="text-lg font-bold text-gray-800 dark:text-white">Admin</h1>}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300">
            {sidebarOpen ? <FiClose /> : <FiMenu />}
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {[
            { id: 'overview', label: 'Overview', icon: FiActivity },
            {
              id: 'assessments', label: 'Assessments', icon: FiFileText, hasSub: true, subTabs: [
                { id: 'user-reports', label: 'User Reports' },
                { id: 'org-reports', label: 'Organizational Reports' }
              ]
            },
            { id: 'notifications', label: 'Notifications', icon: FiMail },
            {
              id: 'settings', label: 'Settings', icon: FiSettings, hasSub: true, subTabs: [
                { id: 'email', label: 'Email Config' },
                { id: 'templates', label: 'Notification Templates' },
                { id: 'webhooks', label: 'Webhooks' },
                { id: 'team', label: 'Team Members' },
                { id: 'developer', label: 'Developer Tools' }
              ]
            },
          ].map(item => (
            <div key={item.id}>
              <button
                onClick={() => handleNavClick(item.id, item.hasSub ? item.subTabs[0].id : null)}
                className={`w-full flex items-center ${sidebarOpen ? 'px-3 py-2' : 'justify-center p-3'} rounded-lg transition-colors ${activeTab === item.id ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}
              >
                <SafeIcon icon={item.icon} className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="ml-3 font-medium">{item.label}</span>}
              </button>
              {item.hasSub && activeTab === item.id && sidebarOpen && (
                <div className="ml-8 mt-2 space-y-1 border-l-2 border-gray-100 dark:border-gray-700 pl-2">
                  {item.subTabs.map(sub => (
                    <button
                      key={sub.id}
                      onClick={(e) => { e.stopPropagation(); handleNavClick(item.id, sub.id); }}
                      className={`block w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors ${activeSubTab === sub.id ? 'text-blue-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 h-16 flex items-center justify-between px-8 z-10">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white capitalize flex items-center">
            {activeTab}
            {activeSubTab && <span className="text-gray-400 text-sm ml-2 flex items-center">/ {activeSubTab.replace('-', ' ')}</span>}
          </h2>
          <div className="flex items-center space-x-4">
            <button 
              onClick={refreshData} 
              className="flex items-center px-4 py-2 bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 rounded-lg transition-all shadow-sm active:scale-95" 
              title="Refresh Data"
            >
              <SafeIcon icon={FiRefreshCw} className={`mr-2 ${isLoading ? "animate-spin" : ""}`} />
              <span className="font-medium hidden md:inline">Refresh Data</span>
            </button>
            <div className="relative">
              <button onClick={() => setProfileMenuOpen(!profileMenuOpen)} className="flex items-center space-x-3 hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg">
                <div className="text-right hidden md:block">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{currentUser?.name}</p>
                </div>
                {currentUser?.avatar_url ? (
                  <img src={currentUser.avatar_url} alt="Avatar" className="w-10 h-10 rounded-full object-cover border-2 border-blue-100" />
                ) : (
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white">{currentUser?.name?.charAt(0)}</div>
                )}
              </button>
              <AnimatePresence>
                {profileMenuOpen && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border py-2 z-50">
                    <button onClick={() => { setShowProfileModal(true); setProfileMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center"><FiEdit2 className="mr-2" /> Edit Profile</button>
                    <div className="px-4 py-2">
                      <label className="flex items-center justify-between cursor-pointer">
                        <span className="text-sm flex items-center"><FiMoon className="mr-2" /> Dark Mode</span>
                        <div className={`w-10 h-5 rounded-full p-1 transition-colors ${preferences.darkMode ? 'bg-blue-600' : 'bg-gray-300'}`} onClick={() => togglePreference('darkMode')}>
                          <div className={`w-3 h-3 bg-white rounded-full shadow-md transform transition-transform ${preferences.darkMode ? 'translate-x-5' : ''}`} />
                        </div>
                      </label>
                    </div>
                    <div className="border-t mt-2 pt-2"><button onClick={handleLogout} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"><FiLogOut className="mr-2" /> Sign Out</button></div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900 p-8">
          {/* OVERVIEW */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { title: 'Total Assessments', value: assessments.length },
                { title: 'Pending Emails', value: pendingEmails.length },
                { title: 'Organizations', value: organizations.length }
              ].map((stat, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border">
                  <p className="text-sm text-gray-500">{stat.title}</p>
                  <p className="text-2xl font-bold">{stat.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* ASSESSMENT > USER REPORTS */}
          {activeTab === 'assessments' && activeSubTab === 'user-reports' && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-bold">Individual User Reports</h3>
                <button onClick={handleExportCSV} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm flex items-center hover:bg-green-700">
                  <FiDownload className="mr-2" /> Export CSV
                </button>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['User', 'Organization', 'Score', 'Date', 'Actions'].map(h => <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {assessments.map((a) => (
                    <tr key={a.id}>
                      <td className="px-6 py-4">{a.user_name}<br /><span className="text-xs text-gray-500">{a.user_email}</span></td>
                      <td className="px-6 py-4">{a.org_code}</td>
                      <td className="px-6 py-4">{a.score}/400</td>
                      <td className="px-6 py-4">{new Date(a.created_at).toLocaleDateString()}</td>
                      <td className="px-6 py-4 flex space-x-2">
                        <button onClick={() => navigate(`/report/${a.org_code}/${a.user_email}`)} className="text-blue-600 hover:text-blue-800"><FiEye /></button>
                        <button onClick={() => generateUserReportPDF(a)} className="text-gray-600 hover:text-gray-800" title="Download PDF"><FiDownload /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ASSESSMENT > ORG REPORTS */}
          {activeTab === 'assessments' && activeSubTab === 'org-reports' && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
                <h3 className="text-lg font-bold mb-4">Default Configuration</h3>
                <div className="flex items-center space-x-4">
                  <label className="text-sm">Global Response Threshold:</label>
                  <input type="number" value={settings.org_report_config.default_threshold} onChange={(e) => setSettings({ ...settings, org_report_config: { ...settings.org_report_config, default_threshold: parseInt(e.target.value) || 1 } })} className="w-20 px-3 py-2 border rounded-lg" min="1" />
                  <button onClick={saveSettings} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Save Default</button>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
                <div className="p-6 border-b"><h3 className="font-bold">Organization Status</h3></div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Organization Name', 'Code', 'Progress', 'Threshold', 'Avg Score', 'Actions'].map(h => <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {getOrgStats().map((org) => (
                      <tr key={org.code}>
                        <td className="px-6 py-4 font-medium">{org.name}</td>
                        <td className="px-6 py-4 text-gray-500">{org.code}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <span className="mr-2 text-sm">{org.count}/{org.threshold}</span>
                            <div className="w-24 bg-gray-200 rounded-full h-1.5">
                              <div className="bg-blue-600 h-1.5 rounded-full" style={{ width: `${Math.min((org.count / org.threshold) * 100, 100)}%` }}></div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {editingOrgThreshold === org.code ? (
                            <div className="flex items-center space-x-1">
                              <input autoFocus className="w-12 px-1 border rounded" defaultValue={org.threshold} onBlur={(e) => handleUpdateOrgThreshold(org.code, e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleUpdateOrgThreshold(org.code, e.currentTarget.value)} />
                            </div>
                          ) : (
                            <button onClick={() => setEditingOrgThreshold(org.code)} className="flex items-center text-gray-600 hover:text-blue-600">
                              {org.threshold}
                              <FiEdit2 className="ml-1 w-3 h-3 opacity-50" />
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4">{Math.round(org.scoreSum / (org.count || 1))}</td>
                        <td className="px-6 py-4">
                          <button disabled={org.count < org.threshold} className={`text-blue-600 ${org.count < org.threshold ? 'opacity-50' : ''}`}>Generate Report</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SETTINGS > EMAIL */}
          {activeTab === 'settings' && activeSubTab === 'email' && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 space-y-6">
              <h3 className="text-lg font-bold mb-4">Postmark Integration</h3>
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg text-sm text-yellow-800 mb-4">
                <strong>Configuration Note:</strong> Please use the "Server API Token" from your Postmark server settings (starts with <code>a5c...</code> in your screenshot) as the API Key below.
              </div>
              <div className="grid gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Server API Token</label>
                  <input type="password" value={settings.postmark_config?.apiKey || ''} onChange={e => setSettings({ ...settings, postmark_config: { ...settings.postmark_config, apiKey: e.target.value } })} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g., a5c9578f-..." />
                  <p className="text-xs text-gray-500 mt-1">Found under Server Settings in Postmark (User screenshot: <code>a5c9578f...</code>)</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">From Email</label>
                    <input type="email" value={settings.postmark_config?.fromEmail || ''} onChange={e => setSettings({ ...settings, postmark_config: { ...settings.postmark_config, fromEmail: e.target.value } })} className="w-full px-3 py-2 border rounded-lg" placeholder="sender@yourdomain.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">From Name</label>
                    <input type="text" value={settings.postmark_config?.fromName || ''} onChange={e => setSettings({ ...settings, postmark_config: { ...settings.postmark_config, fromName: e.target.value } })} className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                </div>
              </div>
              <div className="pt-6 border-t">
                <div className="flex justify-end">
                  <button onClick={saveSettings} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"><FiSave className="mr-2" /> Save Settings</button>
                </div>
              </div>
            </div>
          )}

          {/* SETTINGS > DEVELOPER TOOLS (NEW) */}
          {activeTab === 'settings' && activeSubTab === 'developer' && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 space-y-6">
              <h3 className="text-lg font-bold mb-4 flex items-center"><FiCode className="mr-2" /> Developer Tools</h3>
              <div className="p-6 border border-gray-200 rounded-xl bg-gray-50 dark:bg-gray-700/30">
                <h4 className="font-bold text-gray-900 dark:text-white mb-2">Seed Database with Fake Data</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Use this utility to populate the database with realistic test data. This will create:
                  <ul className="list-disc ml-5 mt-2">
                    <li>3 Organizations (High, Medium, & Low performing)</li>
                    <li>10 Users distributed across these organizations</li>
                    <li>Full assessment responses for each user</li>
                  </ul>
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSeedData}
                    disabled={!!seedStatus}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center shadow-md transition-all active:scale-95 disabled:opacity-50"
                  >
                    <FiDatabase className="mr-2" /> {seedStatus || 'Seed Database'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md my-8">
            <h3 className="text-lg font-bold mb-4">Edit Profile</h3>
            {showCropper && imageSrc ? (
              <div className="mb-4">
                <div className="relative w-full h-64 bg-gray-900 rounded-lg overflow-hidden mb-4">
                  <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={1} onCropChange={setCrop} onCropComplete={onCropComplete} onZoomChange={setZoom} />
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowCropper(false)} className="px-3 py-1.5 bg-gray-200 rounded">Cancel</button>
                  <button onClick={handleSaveCrop} className="px-3 py-1.5 bg-blue-600 text-white rounded">Crop & Save</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Avatar</label>
                  <div className="flex gap-4 items-center">
                    <div onClick={() => fileInputRef.current?.click()} className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300 cursor-pointer overflow-hidden">
                      {profileForm.avatarUrl ? <img src={profileForm.avatarUrl} className="w-full h-full object-cover" /> : <FiImage className="text-gray-400" />}
                    </div>
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 bg-gray-100 rounded-md">Upload Image</button>
                    <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button type="button" onClick={() => setShowProfileModal(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg">Update Profile</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;